import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminSupportOptional, InsertSupportOptionalBody, UpdateSupportOptionalBody } from "./types/supportOptionals";

export const useSupportOptionalsApi = () => {
  const api = useApi();

  const useAdminSupportOptionals = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const mappingUrl = `/admin/settings/patrol/support_optionals`;
      const systemIdUrl = `/admin/settings/patrol/systems/${systemId}/support_optionals`;
      const url = buildUrl(systemId ? systemIdUrl : mappingUrl, query);
      return useSWR<Paginated<AdminSupportOptional>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminSupportOptional>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSupportOptional = useCallback(
    async (body: InsertSupportOptionalBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_optionals`;
      const id = await api.post<{ id: string }, InsertSupportOptionalBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/systems/${body.systemId}/support_optionals`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extras`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminSupportOptional = useCallback(
    async (optionalId: string, body: UpdateSupportOptionalBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_optionals/${optionalId}`;
      await api.put<UpdateSupportOptionalBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/systems/${body.systemId}/support_optionals`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extras`,
      ]);
    },
    [api],
  );

  const updateAdminSupportOptionalVisibility = useCallback(
    async (
      optionalId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { systemId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/patrol/support_optionals/${optionalId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/patrol/support_optionals",
        `/admin/settings/patrol/systems/${options?.systemId}/support_optionals`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${options?.systemId}/support_extras`,
      ]);
    },
    [api],
  );

  const deleteAdminSupportOptional = useCallback(
    async (optionalId: string, systemId?: string): Promise<void> => {
      await api.del(
        systemId
          ? `/admin/settings/patrol/systems/${systemId}/support_optionals/${optionalId}`
          : `/admin/settings/patrol/support_optionals/${optionalId}`,
      );

      mutateCache([
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/support_optionals/${optionalId}`,
        `/admin/settings/patrol/systems/${systemId}/support_optionals`,
        `/admin/settings/patrol/systems/${systemId}/support_optionals/${optionalId}`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${systemId}/support_extras`,
      ]);
    },
    [api],
  );

  return {
    useAdminSupportOptionals,
    insertAdminSupportOptional,
    updateAdminSupportOptional,
    updateAdminSupportOptionalVisibility,
    deleteAdminSupportOptional,
  };
};
